<script lang="ts" setup>
import { Form, FormField, type FormInstance } from '@primevue/forms';
import { AppButton, AppTextInput, AppTypography } from '~/components/base';
import useSendForgotPasswordCode from '~/composables/auth/forgot-password/useSendForgotPasswordCode';
import { EnterUsername as EnterUsernameContent } from '~/utils/contents/auth/forms/ForgotPasswordForm';

const { initialEmail } = defineProps<{
  initialEmail?: string;
}>();

const emit = defineEmits<{
  success: [];
}>();
const modelValue = defineModel<string>();

const formRef = useTemplateRef<FormInstance>('enterUsernameForm');

const { loading, form, errors, resolver, submit } = useSendForgotPasswordCode();

onMounted(() => {
  if (initialEmail) {
    form.value.email = initialEmail;
    formRef.value?.setFieldValue('email', initialEmail);
  }
});

const onFormSubmit = async ({ valid }: { valid: boolean }): Promise<void> => {
  if (!valid) return;

  const result = await submit();
  if (result) {
    modelValue.value = form.value.email;
    emit('success');
  }
};

const canSubmit = computed((): boolean => {
  return !!form.value.email;
});
</script>

<template>
  <Form
    ref="enterUsernameForm"
    :resolver="resolver"
    data-testid="enter-username-form"
    class="flex w-full flex-col"
    @submit="onFormSubmit"
  >
    <div class="enter-username-form__header mb-10 flex flex-col text-center">
      <AppTypography type="title-4" element="h4" class="mb-2">{{
        EnterUsernameContent.title
      }}</AppTypography>
      <AppTypography type="body">{{
        EnterUsernameContent.subtitle
      }}</AppTypography>
    </div>

    <FormField v-slot="$field" name="email" class="mb-6">
      <AppTextInput
        v-model="form.email"
        required
        :label="EnterUsernameContent.fields.email.label"
        :placeholder="EnterUsernameContent.fields.email.placeholder"
        :data-testid="EnterUsernameContent.fields.email.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid || !!errors.email?.length"
        :error-message="$field.error?.message || errors.email?.[0]"
        autocomplete="email"
        class="w-full"
      />
    </FormField>

    <AppButton
      type="submit"
      :disabled="loading || !canSubmit"
      :loading="loading"
      :data-testid="EnterUsernameContent.actions.submitButton.dataTestId"
      :label="EnterUsernameContent.actions.submitButton.label"
    />
  </Form>
</template>

<style></style>
